import type React from 'react';

import { Badge, Text } from '@change/ds-core-ui';
import { TrendingIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { TranslatePluralHtml, withI18nPlaceholders } from '@change-corgi/core-react/i18n';

import I18N_PLACEHOLDERS from '#config/i18n/placeholders/petitionDetails/petition-details.yml';

import type { SignerVelocityPlacement } from '#src/app/pages/petitionDetails/shared/hooks';
import { useSignerVelocityExperiment } from '#src/app/pages/petitionDetails/shared/hooks';

import { SignerVelocityPillImpression } from '../SignerVelocityTracking';

type Props = Readonly<{
	/** Only `'stats_block'` exists today; the prop stays so the pill self-gates against the resolved arm. */
	placement: Exclude<SignerVelocityPlacement, 'none'>;
	/**
	 * Positioning for the wrapper. Owned by the pill rather than the call site so that a non-qualifying
	 * visitor emits nothing at all — a caller-side wrapper would leave an empty, still-spaced div behind.
	 */
	className?: string;
}>;

/**
 * The signer velocity recency pill: a quiet, static count of signatures in the last 24 hours.
 *
 * Keep it static and unanimated. The Q2 2026 Momentum Bar used animated counters and a recent-signers
 * carousel, shipped −0.6% sign rate, and was skipped on first pass by all 7 moderated-study participants.
 *
 * Includes its own impression Waypoint, so it must not be mounted inside a `ResponsiveBox` — both branches
 * of one render, and the impression would double-count.
 */
const SignerVelocityPillComponent: React.FC<Props> = ({ placement, className }) => {
	const {
		placement: activePlacement,
		dailySignatureCount,
		dailySignatureCountLocalized,
	} = useSignerVelocityExperiment();

	if (activePlacement !== placement) {
		return null;
	}

	return (
		<div className={cn('flex', className)}>
			<Badge
				variant="success"
				// Reads as page furniture rather than a callout, per the treatment principles.
				emphasis="weaker"
				size="large"
				leadingIcon={<TrendingIcon />}
				label={
					<Text variant="body" className="ml-2 py-1" size="small">
						<TranslatePluralHtml
							value="corgi.petition_details.signer_velocity.pill_label_html"
							count={dailySignatureCount}
							replacements={{ countLocalized: dailySignatureCountLocalized }}
						/>
					</Text>
				}
				data-qa="signer-velocity-pill"
				data-testid="signer-velocity-pill"
			/>
			<SignerVelocityPillImpression />
		</div>
	);
};

export const SignerVelocityPill = withI18nPlaceholders(I18N_PLACEHOLDERS, SignerVelocityPillComponent);
